import React from "react";
import "./TagInput.css";
import { Box, IconButton, List, ListItem, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import _request from "../../actions/_request";
import PageAbstract from "../PageAbstract";
import AddIcon from "@mui/icons-material/Add";
import GptLogo from "../../images/chatgpt-6.svg";
import i18n from "i18";
import AppContext from "../../AppContext";

class TagInput extends PageAbstract {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      inputValue: "",
      filteredSuggestions: [],
      suggestions: [],
      generatedTags: [],
      isLoading: false,
    };
  }

  async componentDidMount() {
    await this.loadData();
  }

  async loadData() {
    try {
      const resp = await _request.posts.viewTags();
      this.setState({
        suggestions: resp || [],
      });
    } catch (error) {
      console.error("Error loading tags:", error);
    }
  }

  handleInputChange = (e) => {
    const { value } = e.target;
    const { suggestions } = this.state;

    const suggestionsArray = Object.values(suggestions);

    const filteredSuggestions = suggestionsArray.filter((suggestion) =>
      suggestion.toLowerCase().includes(value.toLowerCase())
    );

    this.setState({
      inputValue: value,
      filteredSuggestions: filteredSuggestions,
    });
  };

  handleAddTag = (tag) => {
    const { tags, onAddTag } = this.props;
    const normalizedTag = tag.startsWith("#") ? tag : `#${tag}`;

    if (
      normalizedTag &&
      !tags.some(
        (existingTag) =>
          existingTag.toLowerCase() === normalizedTag.toLowerCase()
      )
    ) {
      onAddTag(normalizedTag);
      this.setState({ inputValue: "", filteredSuggestions: [] });
    }
  };

  handleRemoveTag = (index) => {
    this.props.onRemoveTag(index);
  };

  handleKeyDown = (e) => {
    const { inputValue } = this.state;
    if (e.key === "Enter" && inputValue) {
      this.handleAddTag(inputValue);
    }
  };

  handleSuggestionClick = (suggestion) => {
    this.handleAddTag(suggestion);
  };

  handleGenerateTag = async () => {
    const { text } = this.props;
    const obj = { text };

    this.setState({ isLoading: true });

    try {
      const result = await _request.accounts.generateTags(obj);

      const hashtagsStr =
        typeof result?.data === "string"
          ? result.data
          : result?.data?.hashtags || "";

      const tags = hashtagsStr
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.startsWith("#"));

      tags.forEach((tag) => this.handleAddTag(tag));

      // Any AI-backed action consumes credits — refresh the header counter so
      // the user sees the new balance without a page reload.
      if (result?.data?.credits_remaining !== undefined && this.context?.refreshCredits) {
        this.context.refreshCredits();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { tags } = this.props;
    const { inputValue, filteredSuggestions, generatedTags, isLoading } =
      this.state;
    const combinedTags = [...tags, ...generatedTags];

    return (
      <Box className="w-full">
        <Box className="mb-4">
          <input
            type="text"
            value={inputValue}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            placeholder={i18n.t("postPage.addTag")}
            className="tag-input-field"
          />
        </Box>
        <Box className="tags-container">
          {combinedTags.map((tag, index) => (
            <ListItem
              key={index}
              className="tag"
              onClick={() => this.handleRemoveTag(index)}>
              <span title={tag}>{tag}</span>
              <IconButton>
                <CloseIcon sx={{ width: "20px", height: "20px" }} />
              </IconButton>
            </ListItem>
          ))}
          {inputValue && (
            <ListItem
              className="tag cursor-pointer"
              onClick={() => this.handleAddTag(inputValue)}>
              <span title={inputValue}>{inputValue}</span>
              <IconButton>
                <AddIcon sx={{ width: "20px", height: "20px" }} />
              </IconButton>
            </ListItem>
          )}
        </Box>
        <Box className="flex justify-end mt-2">
          <Tooltip
            title={this.props.text ? "" : i18n.t("tagInput.tooltip")}
            arrow
            disableHoverListener={!(!isLoading && !this.props.text)}>
            <Box
              className="btnHastag"
              onClick={
                !isLoading && this.props.text ? this.handleGenerateTag : null
              }
              sx={{
                cursor:
                  isLoading || !this.props.text ? "not-allowed" : "pointer",
                opacity: isLoading || !this.props.text ? 0.5 : 1,
              }}>
              <img
                src={GptLogo}
                alt="Gpt Logo"
                style={{
                  width: "20px",
                  height: "20px",
                  animation: isLoading ? "spin 1s linear infinite" : "none",
                }}
              />
              {isLoading
                ? i18n.t("tagInput.loading")
                : i18n.t("tagInput.generateHashtags")}
            </Box>
          </Tooltip>
        </Box>

        {filteredSuggestions.length > 0 && (
          <List className="suggestions scrolling">
            {filteredSuggestions.map((suggestion, index) => (
              <ListItem
                key={index}
                className="suggestion"
                onClick={() => this.handleSuggestionClick(suggestion)}
                button>
                {suggestion}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  }
}

export default TagInput;
